// src/cqcColors.js — shared colour helpers, same green->red language as
// RegulatoryCompliance.js's pctBg/pctBgStrong, plus CQC's own 4-band rating
// scale (Outstanding / Good / Requires improvement / Inadequate).

import React from "react";

export function pctBg(pct) {
  if (pct == null || Number.isNaN(pct)) return "#eee";
  const t = Math.max(0, Math.min(1, (pct - 10) / 90));
  const hue = Math.round(120 * t);
  return `hsl(${hue}, 75%, 85%)`;
}

export function pctBgStrong(pct) {
  if (pct == null || Number.isNaN(pct)) return "#ddd";
  const t = Math.max(0, Math.min(1, (pct - 10) / 90));
  const hue = Math.round(120 * t);
  return `hsl(${hue}, 70%, 72%)`;
}

export const fmtPct = (n) => (n == null || Number.isNaN(n) ? "—" : `${Math.round(n)}%`);

const RATING_META = {
  "Outstanding":            { bg: "#dcfce7", fg: "#166534", border: "#86efac" },
  "Good":                   { bg: "#e0f2fe", fg: "#075985", border: "#7dd3fc" },
  "Requires improvement":   { bg: "#fef9c3", fg: "#854d0e", border: "#fde047" },
  "Inadequate":             { bg: "#fee2e2", fg: "#991b1b", border: "#fca5a5" },
};
export function ratingMeta(rating) {
  return RATING_META[rating] || { bg: "#f1f5f9", fg: "#64748b", border: "#e2e8f0" };
}

export function RatingBadge({ rating }) {
  const m = ratingMeta(rating);
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", borderRadius: 999, fontSize: 11,
      fontWeight: 600, background: m.bg, color: m.fg, border: `1px solid ${m.border}`,
      whiteSpace: "nowrap",
    }}>
      {rating || "Not yet rated"}
    </span>
  );
}

const REQ_META = {
  mandatory:   { label: "Mandatory",   bg: "#fee2e2", fg: "#991b1b", border: "#fca5a5" },
  recommended: { label: "Recommended", bg: "#fef3c7", fg: "#92400e", border: "#fcd34d" },
  optional:    { label: "Optional",    bg: "#e0e7ff", fg: "#3730a3", border: "#a5b4fc" },
};
export function ReqBadge({ requirement }) {
  const m = REQ_META[requirement] || REQ_META.optional;
  return (
    <span style={{
      display: "inline-block", padding: "1px 7px", borderRadius: 999, fontSize: 10,
      fontWeight: 600, background: m.bg, color: m.fg, border: `1px solid ${m.border}`,
    }}>
      {m.label}
    </span>
  );
}

// Worklist status colours — see cqcRollup.js's elementStatus() for how a
// status is derived (UPDATE 7: manually set via a dropdown, not inferred).
// Red is "not-started", amber is "ongoing", green is "completed", grey
// ("not-applicable") is used sparingly since the Worklist filters those rows
// out by default. "overdue"/"in-progress" are kept defined (same colours as
// their nearest replacement) purely so nothing throws if older stored data
// or another view still passes one in — elementStatus() itself no longer
// produces them.
const STATUS_META = {
  "not-started":    { label: "Not started", dot: "#dc2626", bg: "#fee2e2", fg: "#991b1b", border: "#fca5a5" },
  "overdue":        { label: "Overdue",     dot: "#dc2626", bg: "#fee2e2", fg: "#991b1b", border: "#fca5a5" },
  "ongoing":        { label: "In progress", dot: "#d97706", bg: "#fef3c7", fg: "#92400e", border: "#fcd34d" },
  "in-progress":    { label: "In progress", dot: "#d97706", bg: "#fef3c7", fg: "#92400e", border: "#fcd34d" },
  "completed":      { label: "Completed",   dot: "#16a34a", bg: "#dcfce7", fg: "#166534", border: "#86efac" },
  "not-applicable": { label: "N/A",         dot: "#94a3b8", bg: "#f1f5f9", fg: "#64748b", border: "#e2e8f0" },
};
export function statusMeta(status) {
  return STATUS_META[status] || STATUS_META["not-started"];
}
export function StatusBadge({ status }) {
  const m = statusMeta(status);
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5, padding: "2px 9px", borderRadius: 999,
      fontSize: 11, fontWeight: 700, background: m.bg, color: m.fg, border: `1px solid ${m.border}`,
      whiteSpace: "nowrap",
    }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: m.dot, flexShrink: 0 }} />
      {m.label}
    </span>
  );
}

// Pale full-row tint + a bold left edge in the same hue, so a Worklist row's
// background itself communicates status at a glance (UPDATE 7: "the status
// changes the row's background colour") without fighting the badge/text
// contrast — these are deliberately much lighter than STATUS_META's badge
// colours, which stay saturated since they sit on white.
const ROW_TINT = {
  "not-started":    { background: "#fef2f2", borderLeft: "4px solid #ef4444" },
  "ongoing":        { background: "#fffbeb", borderLeft: "4px solid #f59e0b" },
  "completed":      { background: "#f0fdf4", borderLeft: "4px solid #22c55e" },
  "not-applicable": { background: "#f8fafc", borderLeft: "4px solid #cbd5e1" },
};
export function statusRowStyle(status) {
  return ROW_TINT[status] || ROW_TINT["not-started"];
}
